import cn from 'classnames';
import { useSelect } from 'downshift';
import uniqueId from 'lodash-es/uniqueId';
import * as React from 'react';
import { DetailedHTMLProps, HTMLAttributes, useRef, useState } from 'react';
import { useCommonInputStyles } from '@tcl-boron-foundations/input';
import { CaretDown, Close } from '@tcl-boron-icons/icons';
import { BaseInputProps, DropdownOption } from '@tcl-argon-models/models';
import { mergeClasses, StatusVariant } from '@tcl-argon-utils/utils';
import { useSingleSelectDropdownStyles } from './styles';
import { useScrollbarStyles } from '@tcl-boron-styles/hooks';
import { usePopper } from 'react-popper';

type DivElement = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export interface SingleSelectDropdownProps extends BaseInputProps, Omit<DivElement, 'onChange'> {
  /** If `true`, show an "X" button that, when clicked, will clear a user's current selection. */
  clearable?: boolean;
  /**
   * A handler called whenever this input changes.
   * The selected option is passed as the only argument to the callback.
   */
  onChange: (selectedItem: DropdownOption | null) => void;
  /** An array of DropdownOptions that use user can select. */
  options: DropdownOption[];
  /** A DropdownOption that represent a user's current selection. */
  value?: DropdownOption | null;
  /** status either 'success' | 'error' | 'warning' | 'default' changes colors to visually reflect status of dropdown */
  status?: StatusVariant;
  /** whether the dropdown renders as primary or secondary style, the main difference being border radius */
  dropdownType?: 'primary' | 'secondary';
}

export const SingleSelectDropdown: React.FC<SingleSelectDropdownProps> = ({
  className = '',
  classes: classOverrides = {},
  clearable = false,
  disabled = false,
  hideLabel = false,
  id,
  label,
  onChange,
  options: items,
  placeholder = 'Select',
  subContent,
  value: selectedItem = null,
  status = 'default',
  dropdownType = 'primary',
  ...props
}) => {
  const inputRef = useRef(null);
  const popperRef = useRef(null);

  const { styles, attributes } = usePopper(inputRef.current, popperRef.current, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 4],
        },
      },
    ],
  });
  [id] = useState(id || uniqueId('single_select_dropdown_'));

  const itemToString = (option: DropdownOption) => (option ? option.label : '');
  const tabIndex = disabled ? -1 : 0;
  const classes = mergeClasses(
    useScrollbarStyles(),
    useCommonInputStyles({ status, subContent }),
    useSingleSelectDropdownStyles({
      isPrimary: dropdownType === 'primary',
      isDisabled: disabled,
      status,
      isEmpty: selectedItem !== null,
    }),
    classOverrides,
    { root: className },
  );

  const { getItemProps, getLabelProps, getMenuProps, getToggleButtonProps, highlightedIndex, isOpen, reset } =
    useSelect<DropdownOption>({
      id,
      itemToString: (option: DropdownOption | null) => (option !== null ? option.label : ''),
      items,
      onSelectedItemChange: ({ selectedItem }) => (selectedItem !== undefined ? onChange(selectedItem) : () => {}),
      selectedItem,
    });

  const clearSelection = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    reset();
  };

  const shouldShowClearbutton = clearable && selectedItem != null;

  return (
    <div className={classes.root} data-analytics="single-select-dropdown" {...props}>
      <label
        {...getLabelProps({ className: cn(classes.label, { [classes.isVisuallyHidden]: hideLabel }), htmlFor: id })}
      >
        {label}
      </label>
      <div className={classes.inputWrapper}>
        <div
          {...getToggleButtonProps({
            className: cn(classes.valueContainer, { [classes.disabled]: disabled }),
            tabIndex,
            disabled,
            ref: inputRef,
          })}
        >
          {selectedItem ? (
            <p className={classes.value}>{itemToString(selectedItem)}</p>
          ) : (
            <p className={cn(classes.value, classes.placeholder)}>{placeholder}</p>
          )}
          {shouldShowClearbutton && (
            <button
              aria-label="clear selection"
              className={classes.clearButton}
              disabled={disabled}
              onClick={clearSelection}
            >
              <Close height="10px" width="10px" />
            </button>
          )}
          <CaretDown height="10px" width="10px" />
        </div>
        <ul
          {...getMenuProps({
            className: cn(classes.menu, { [classes.invisibleMenu]: !isOpen }, classes.scrollbar),
            ref: popperRef,
            style: styles.popper,
            ...attributes.popper,
          })}
        >
          {items.map((item, index) => {
            const isSelected = selectedItem != null && item.value === selectedItem.value;

            return (
              <li
                key={item.value}
                {...getItemProps({
                  item,
                  index,
                  className: cn(classes.menuItem, {
                    [classes.highlightedMenuItem]: highlightedIndex === index,
                    [classes.selectedMenuItem]: isSelected,
                  }),
                })}
              >
                {itemToString(item)}
              </li>
            );
          })}
        </ul>
      </div>
      {subContent && <p className={classes.subContent}>{subContent}</p>}
    </div>
  );
};
